import type { ModuleFull, OrganicSoilResponse } from "@features/modules/organicSoil/types"
import { FEModules } from "../../../utils/moduleList";
import { FEModulesObj } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createModuleGetApi, createModuleUpdateApi } from "./factories/createModuleApi";
import { transformModuleResponse, prepareModulePayload } from "./factories/moduleApiUtils";

const includedPropList = [
	"id",
	"land_use_change",
	"note",
	"activity",
	"status",
	"parent_land_use_type_start",
	"parent_land_use_type_w",
	"parent_land_use_type_wo",
	"area",
	"drainage_area_start",
	"drainage_area_w",
	"drainage_area_wo",
	"area_not_drained_start",
	"area_not_drained_w",
	"area_not_drained_wo",
	"ditches_area_start",
	"ditches_area_w",
	"ditches_area_wo",
	"soil_fire_periodicity_start",
	"soil_fire_periodicity_w",
	"soil_fire_periodicity_wo",
	"soil_fire_impact_percentage_start",
	"soil_fire_impact_percentage_w",
	"soil_fire_impact_percentage_wo",
	"peat_area_start",
	"peat_area_w",
	"peat_area_wo",
	"peat_ditches_area_start",
	"peat_ditches_area_w",
	"peat_ditches_area_wo",
	"peat_extraction_height_start",
	"peat_extraction_height_w",
	"peat_extraction_height_wo",
	"is_peat_for_energy_start",
	"is_peat_for_energy_w",
	"is_peat_for_energy_wo",
	"land_use_type_start",
	"land_use_type_w",
	"land_use_type_wo",
	"fire_type_start",
	"fire_type_w",
	"fire_type_wo",
	"peat_type",
	"land_use_type_thread",
	"drainage_area_thread",
	"area_not_drained_thread",
	"ditches_area_thread",
	"fire_type_thread",
	"soil_fire_periodicity_thread",
	"soil_fire_impact_percentage_thread",
	"peat_type_thread",
	"peat_area_thread",
	"peat_ditches_area_thread",
	"peat_extraction_height_thread",
	"is_peat_for_energy_thread",
];
const includedT2PropList = [
	"soc_t2_start",
	"soc_t2_w",
	"soc_t2_wo",
	"flu_t2_start",
	"flu_t2_w",
	"flu_t2_wo",
	"fi_t2_start",
	"fi_t2_w",
	"fi_t2_wo",
	"fmg_t2_start",
	"fmg_t2_w",
	"fmg_t2_wo",
	"onsite_co2_drainage_t2_start",
	"onsite_co2_drainage_t2_w",
	"onsite_co2_drainage_t2_wo",
	"onsite_ch4_drainage_t2_start",
	"onsite_ch4_drainage_t2_w",
	"onsite_ch4_drainage_t2_wo",
	"onsite_n2o_drainage_t2_start",
	"onsite_n2o_drainage_t2_w",
	"onsite_n2o_drainage_t2_wo",
	"offsite_doc_drainage_t2_start",
	"offsite_doc_drainage_t2_w",
	"offsite_doc_drainage_t2_wo",
	"offsite_ch4_drainage_t2_start",
	"offsite_ch4_drainage_t2_w",
	"offsite_ch4_drainage_t2_wo",
	"onsite_co2_rewetting_t2_start",
	"onsite_co2_rewetting_t2_w",
	"onsite_co2_rewetting_t2_wo",
	"onsite_ch4_rewetting_t2_start",
	"onsite_ch4_rewetting_t2_w",
	"onsite_ch4_rewetting_t2_wo",
	"onsite_n2o_rewetting_t2_start",
	"onsite_n2o_rewetting_t2_w",
	"onsite_n2o_rewetting_t2_wo",
	"offsite_doc_rewetting_t2_start",
	"offsite_doc_rewetting_t2_w",
	"offsite_doc_rewetting_t2_wo",
	"mean_dry_matter_t2_start",
	"mean_dry_matter_t2_w",
	"mean_dry_matter_t2_wo",
	"fire_on_soil_co2_t2_start",
	"fire_on_soil_co2_t2_w",
	"fire_on_soil_co2_t2_wo",
	"fire_on_soil_co_t2_start",
	"fire_on_soil_co_t2_w",
	"fire_on_soil_co_t2_wo",
	"fire_on_soil_ch4_t2_start",
	"fire_on_soil_ch4_t2_w",
	"fire_on_soil_ch4_t2_wo",
	"onsite_co2_peat_t2_start",
	"onsite_co2_peat_t2_w",
	"onsite_co2_peat_t2_wo",
	"onsite_ch4_peat_t2_start",
	"onsite_ch4_peat_t2_w",
	"onsite_ch4_peat_t2_wo",
	"onsite_n2o_peat_t2_start",
	"onsite_n2o_peat_t2_w",
	"onsite_n2o_peat_t2_wo",
	"offsite_doc_peat_t2_start",
	"offsite_doc_peat_t2_w",
	"offsite_doc_peat_t2_wo",
	"offsite_ch4_peat_t2_start",
	"offsite_ch4_peat_t2_w",
	"offsite_ch4_peat_t2_wo",
	"peat_density_t2_start",
	"peat_density_t2_w",
	"peat_density_t2_wo",
];

// Define constants for the module
const MODULE_TYPE = "OrganicSoil";
const TAG: TagTypes = "OrganicSoil";
const TAG_DEFAULTS: TagTypes = "OrganicSoilDefaults";
const ENDPOINT = FEModulesObj[FEModules.OrganicSoil].path;

const t1PercentageProps = ["ditches_area", "soil_fire_impact_percentage", "peat_ditches_area"];

const transformResponse = (response: OrganicSoilResponse) => transformModuleResponse<OrganicSoilResponse, ModuleFull>({
	response,
	includedPropList,
	includedT2PropList,
	percentageProps: t1PercentageProps
})

const organicSoilApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse,
})

const publicOrganicSoilApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse,
})

const transformUpdatePayload = (data: Partial<ModuleFull>, activityId: number) => prepareModulePayload({
	moduleData: data.module,
	tiertwoData: data.tiertwo,
	percentageProps: t1PercentageProps,
	activityId
})

const mutationApiSlice = createModuleUpdateApi<ModuleFull, OrganicSoilResponse>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformUpdatePayload,
})

export const {
	useGetOrganicSoilQuery,
	useGetOrganicSoilDefaultsQuery
} = organicSoilApiSlice;

export const {
	useGetOrganicSoilQuery: usePublicGetOrganicSoilQuery,
	useGetOrganicSoilDefaultsQuery: usePublicGetOrganicSoilDefaultsQuery
} = publicOrganicSoilApiSlice;

export const {
	useUpdateOrganicSoilMutation,
} = mutationApiSlice;

export const organicSoilEndpoints = organicSoilApiSlice.endpoints;
export const organicSoilMutationEndpoints = mutationApiSlice.endpoints;
